import React, { useState } from 'react';

// ── NEWSLETTER SECTION ────────────────────────────────────────
// Clicking "Subscribe" submits straight to Netlify Forms (no page
// reload, no email app opening). Netlify emails the notification
// address set under Site configuration → Forms → Form notifications
// in the Netlify dashboard — point that at shpevt-g@vt.edu (or
// whoever checks it) so they can add the person by hand.
//
// Netlify can only detect this form if a matching hidden form exists
// in public/index.html — see the comment above the <form> below.
// Free tier covers 100 submissions/month.
//
// This used to also list recent newsletter issues, which had to be
// updated by hand every semester. That's been removed — this section
// is now just the signup card, nothing to maintain.

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | error

  const encode = (data) =>
    Object.keys(data)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
      .join('&');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'newsletter-signup', email }),
      });
      setSubmitted(true);
      setEmail('');
      setStatus('idle');
      setTimeout(() => setSubmitted(false), 4000);
    } catch (err) {
      setStatus('error');
    }
  };

  return (
    <section id="newsletter" style={{ padding: '80px 24px', background: 'white' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>

        <span className="section-label">Newsletter</span>
        <h2 className="section-title">Stay Connected</h2>
        <p style={{ color: '#666', fontSize: 15, lineHeight: 1.7, marginBottom: 36, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
          Our newsletter covers upcoming events, scholarship deadlines, conference updates, and member spotlights — delivered straight to your inbox.
        </p>

        <div style={{
          background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
          borderRadius: 24, padding: '48px 40px', color: 'white',
          textAlign: 'left',
        }}>
          <div style={{ fontSize: 48, marginBottom: 20, textAlign: 'center' }}>✉️</div>
          <h3 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 26, fontWeight: 900, marginBottom: 12,
            textAlign: 'center',
          }}>
            Subscribe to the SHPE@VT Newsletter
          </h3>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.7, marginBottom: 32, textAlign: 'center' }}>
            Get event reminders, scholarship alerts, and chapter updates delivered monthly. No spam — unsubscribe anytime.
          </p>

          {submitted ? (
            <div style={{
              background: 'rgba(45,122,79,0.25)',
              border: '1px solid rgba(45,122,79,0.5)',
              borderRadius: 12, padding: '20px 24px',
              textAlign: 'center', color: '#5DE88A',
            }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
              <div style={{ fontWeight: 700 }}>You're subscribed!</div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>We'll add you to the list shortly.</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} name="newsletter-signup">
              <div style={{ marginBottom: 12 }}>
                <input
                  type="email"
                  placeholder="your.email@vt.edu"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  disabled={status === 'loading'}
                  style={{
                    width: '100%', padding: '14px 18px',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.08)',
                    color: 'white', fontSize: 15,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>

              {status === 'error' && (
                <p style={{ color: '#F28B82', fontSize: 13, marginBottom: 12 }}>
                  Something went wrong — please try again in a moment.
                </p>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={status === 'loading'}
                style={{
                  width: '100%', justifyContent: 'center', fontSize: 15, padding: '14px',
                  opacity: status === 'loading' ? 0.7 : 1,
                  cursor: status === 'loading' ? 'default' : 'pointer',
                }}
              >
                {status === 'loading' ? 'Subscribing…' : 'Subscribe →'}
              </button>
            </form>
          )}

          {/* Social follow reminder */}
          <div style={{
            marginTop: 28, paddingTop: 24,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Also follow us:</span>
            <a href="https://www.instagram.com/shpe_vt/" target="_blank" rel="noopener noreferrer" style={{
              background: 'rgba(255,255,255,0.1)', color: 'white',
              padding: '6px 14px', borderRadius: 100, fontSize: 12, fontWeight: 600,
            }}>
              📷 Instagram
            </a>
            <a href="https://www.linkedin.com/company/society-of-hispanic-professional-engineers/" target="_blank" rel="noopener noreferrer" style={{
              background: 'rgba(255,255,255,0.1)', color: 'white',
              padding: '6px 14px', borderRadius: 100, fontSize: 12, fontWeight: 600,
            }}>
              in LinkedIn
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}